import { client, dbClient, s3Client } from './awsClients';
import { PutItemCommand, PutItemCommandInput, ScanCommand, QueryCommand, UpdateItemCommand, UpdateItemInput, GetItemCommand, QueryCommandInput, UpdateItemCommandInput } from '@aws-sdk/client-dynamodb';
import { HOSPITAL_BLOCK_NAME, HOSPITAL_BLOCK_TYPE, HOSPITAL_ID, DB_PARENT_ID, TENANT_ID, UUID, CREATED_BY, CREATED_AT, MODIFIED_BY, MODIFIED_AT, DELETED_BY, DELETED_AT } from '@constants/dynamoDBAttributes';
import { v4 as uuidv4 } from 'uuid';
import { capitalizeFirstLetter, formatDateDMY, generateUniqueTimestamp, getDataUsingTenantId, getDataUuid, userDetail, userInfo } from './sharedUtils';
import { DEVICE_TABLE, HOSPITALS_TABLE,NURSE_DEVICE_TABLE } from '@constants/dynamoDBTableName';
import { APPROVED, NURSE } from '@constants/constants';
import { AttributeValue } from '@aws-sdk/client-dynamodb';
import { updateUser } from './utils';
import { GENERATE_REPORT_PDF_API } from '@constants/auth/Api';
import { API_KEY } from '@constants/AppConfig';



export async function addHospitalBlock(data: any, tenantId: string, hospital_uuid: string) {
  const formattedDateTime = generateUniqueTimestamp();
  const params: PutItemCommandInput = {
    TableName: "hospital_block",
    Item: {
      [UUID]: { S: data.id },
      [HOSPITAL_BLOCK_NAME]: { S: data.name },
      [TENANT_ID]: { S: tenantId },
      [HOSPITAL_ID]: { S: hospital_uuid },
      [DB_PARENT_ID]: { S: data.parent },
      [CREATED_BY]: { S: tenantId },
      [CREATED_AT]: { S: data.date },
      [MODIFIED_BY]: { S: tenantId },
      [MODIFIED_AT]: { S: formattedDateTime },
      [DELETED_BY]: { S: '' },
      [DELETED_AT]: { S: '' },
    },
  };

  try {
    const command = new PutItemCommand(params);
    const result = await dbClient.send(command);
    // console.log("Data saved successfully:", result);
  } catch (error: any) {
    // console.error("Error saving data to DynamoDB:", error);
    let errorMessage = 'Error adding data to DynamoDB'
    switch (error.name) {
      case 'ValidationException':
        errorMessage = 'Validation error occurred. Please check your data.';
      case 'ResourceNotFoundException':
        errorMessage = 'DynamoDB table not found.';
      default:
        errorMessage = 'An error occurred while saving data. Please try again later.';
    }

    return errorMessage;
  }
}

export async function hospitalBlocks(tableName: string, tenantId: string, type: string, columnName: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#tenantId = :tenantId AND #value = :value AND #deleted_at = :deleted_at",
    ExpressionAttributeNames: {
      "#tenantId": "tenant_id",
      "#value": columnName,
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":tenantId": { S: tenantId },
      ":value": { S: type },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    // console.log('scanHospitalInfo', response.Items);
    if (response.Items) {
      // const names = response.Items.map(item => item.name.S);
      // const names = Array.isArray(response.Items) ? response.Items.map(item => item.name?.S).filter(name => name !== undefined) : [];
      return response.Items;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function checkHospitalBlocks(tableName: string, tenantId: string, parentId: string) {
  const params = {
    TableName: tableName,
    FilterExpression: "#tenantId = :tenantId AND #parent_id = :parent_id AND #deleted_at = :deleted_at", // Removed #value
    ExpressionAttributeNames: {
      "#tenantId": "tenant_id",
      "#parent_id": "parent_id",  // Added parent_id mapping
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":tenantId": { S: tenantId },
      ":parent_id": { S: parentId },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    if (response.Items) {
      return response.Items;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}
export async function checkParentInDeviceChangeLog(tableName: string, uuId: string) {
  const params = {
    TableName: tableName,
    IndexName: 'hospital_block_id-created_at-index',
    FilterExpression: "#hospital_block_id = :hospital_block_id AND #deleted_at = :deleted_at", // Removed #value
    ExpressionAttributeNames: {
      "#hospital_block_id": "hospital_block_id",
      "#deleted_at": "deleted_at"
    },
    ExpressionAttributeValues: {
      ":hospital_block_id": { S: uuId },
      ":deleted_at": { S: '' }
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    if (response.Items) {
      return response.Items;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}


export async function hospitalParentData(hospitalId: string) {
  const params = {
    TableName: 'hospital_block',
    IndexName: 'hospital_id-created_at-index',
    KeyConditionExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response.Items) {
      const hospitalParentData = response.Items.filter(item => !item.deleted_at || item.deleted_at.S === '');
      // console.log('hospitalParentData', hospitalParentData);
      return hospitalParentData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function hospitalInfo(tenantId: string) {
  const params = {
    TableName: 'hospitals',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    const info = await userInfo(tenantId);

    if (response.Items && response.Items.length != 0 && info && (info != null || info != 'null')) {
      const hospitalData = response.Items
        .filter(item => !item.deleted_at || item.deleted_at.S === '')
        .map(async (item) => {
          const createdBy = item.created_by?.S ? await createdByName(item.created_by.S) : '';
          const createdAT = item.created_at?.S ? formatDateDMY(item.created_at.S) : '';
          return {
            name: info.name?.S ? info.name?.S : '',
            address: `${info.address1?.S}, ${info.address2?.S}, ${info.city?.S}, ${info.state?.S}, ${info.country?.S} - ${info.pincode?.S}`,
            created_at: createdAT,
            created_by: createdBy,
            hospital_id: item.hospital_id?.S,
            hospital_name: item.hospital_name?.S,
            modified_at: item.modified_at?.S,
            modified_by: item.modified_by?.S,
            tenant_id: item.tenant_id?.S,
            user_id: item.user_id?.S,
            uuid: item.uuid?.S,
          };
        });

      // console.log('userInfo', hospitalData)
      return hospitalData.length > 0 ? hospitalData[0] : null;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}
export async function hospitalInfoData(tenantId: string) {
  const params = {
    TableName: 'hospital_info',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    return response.Items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}
export async function createdByName(tenantId: string) {
  const params = {
    TableName: 'user_info',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    // const createdBy = response.Items.filter(item => !item.deleted_at || item.deleted_at.S === '');
    if (response.Items && response.Items.length > 0) {
      const createdBy = response.Items[0];
      // console.log('createdBy', createdBy);
      return createdBy.name?.S || null; // Assuming the user name is stored in `name` attribute
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function updateTable(tableName: string, key: any, updateExpression: string, expressionAttributeNames: any, expressionAttributeValues: any) {
  const params: UpdateItemInput = {
    TableName: tableName,
    Key: key,
    UpdateExpression: updateExpression,
    ExpressionAttributeNames: expressionAttributeNames,
    ExpressionAttributeValues: expressionAttributeValues,
    ReturnValues: "ALL_NEW" // Optional parameter to return updated item
  };

  try {
    const command = new UpdateItemCommand(params);
    const response = await dbClient.send(command);
    // console.log(`Updated item in table ${tableName}:`, response.Attributes);
    return response.Attributes; // Return the updated item attributes if needed
  } catch (error) {
    console.error(`Error updating item in table ${tableName}:`, error);
    throw error;
  }
}


export async function handleHospitalBlockDelete(tenant_id: string, table_uuid: string) {
  // console.log('data', tenant_id, userId)
  try {
    const formattedDateTime = generateUniqueTimestamp();
    const updateExpression = "SET #modified_by = :tenant_id, #modified_at = :formattedDateTime, #deleted_by = :tenant_id, #deleted_at = :formattedDateTime";
    const expressionAttributeNames = {
      "#modified_by": "modified_by",
      "#modified_at": "modified_at",
      "#deleted_by": "deleted_by",
      "#deleted_at": "deleted_at"
    };
    const expressionAttributeValues = {
      ":tenant_id": { S: tenant_id },
      ":formattedDateTime": { S: formattedDateTime }
    };

    await updateTable("hospital_block", { uuid: { S: table_uuid } }, updateExpression, expressionAttributeNames, expressionAttributeValues);
    // Continue with updates for other tables as needed
  } catch (error: any) {
    console.error("Error updating multiple tables:", error);
    throw error;
  }
}


export async function hospitalUserInfo(tenantId: string) {
  const params = {
    TableName: 'hospitals',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);

    if (response.Items && response.Items.length != 0) {
      const hospitalData = response.Items
        .filter(item => !item.deleted_at || item.deleted_at.S === '')
        .map(async (item) => {
          return {
            hospital_id: item.hospital_id?.S,
            hospital_name: item.hospital_name?.S,
            tenant_id: item.tenant_id?.S,
            uuid: item.uuid?.S,
          };
        });

      // console.log('userInfo', hospitalData)
      return hospitalData.length > 0 ? hospitalData[0] : null;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

interface Nurse {
  tenant_id: string;
  user_id: string;
  name: string;
  uuid: string;
}

interface Device {
  uuid: string;
  device_id: string;
  hospital_id: string;
}

interface HospitalData {
  uuid: string;
  deviceList: Device[];
  nurseList: Nurse[];
}


export async function hospitalUuid(tenantId: string) {

  try {
    const response = await getDataUsingTenantId(HOSPITALS_TABLE, tenantId)
    if (response.Items && response.Items.length != 0) {
      const hospital = response.Items[0];
      const hospital_uuid = hospital.uuid?.S;
      const hospital_id = hospital.hospital_id?.S;

      let deviceList: Device[] = [];
      let nurseList: Nurse[] = [];

      if (hospital_uuid) {
        deviceList = await hospitalDevice(hospital_uuid);
      }
      if (hospital_id) {
        nurseList = await hospitalNurse(hospital_id);
      }

      return {
        deviceList: deviceList,
        nurseList: nurseList
      };
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}
 

export async function hospitalDevice(uuid: string) {
  const params = {
    TableName: 'hospital_device',
    IndexName: 'hospital_id-created_at-index',
    KeyConditionExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: uuid }
    },
  };

  try {
    let items: Device[] = [];
    let deviceData: Device[] = [];
    let lastEvaluatedKey: any = null;

    do {
      const command = new QueryCommand({
        ...params,
        ExclusiveStartKey: lastEvaluatedKey,
      });
      const response = await dbClient.send(command);
      const filteredItems = (response.Items || [])
        .filter(item => !item.deleted_at?.S)
        .map(item => ({
          uuid: item.uuid?.S || '',
          device_id: item.device_id?.S || '',
          hospital_id: item.hospital_id?.S || '',
        }));
      items.push(...filteredItems);
      lastEvaluatedKey = response.LastEvaluatedKey;
    } while (lastEvaluatedKey);

    for (let device of items) {
      let deviceDetails = await getDeviceDetails(device.device_id, device.hospital_id);
      //  console.log('deviceDetails', deviceDetails)
      if (deviceDetails != null) {
        deviceData.push(deviceDetails)
      }
    }

    return deviceData;
  }
  catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function getDataUsingDeviceUuid(deviceId: string) {
  const params = {
    TableName: NURSE_DEVICE_TABLE,
    IndexName: 'device_id-created_at-index',
    KeyConditionExpression: 'device_id = :device_id',
    FilterExpression: "deleted_at = :deleted_at AND has_control = :has_control",
    ExpressionAttributeValues: {
      ':device_id': { S: deviceId },
      ':deleted_at': { S: '' },
      ':has_control': { BOOL: true }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  }
  catch (error) {
    console.log('error', error)
    throw error;
  }
}

interface NurseUserDataItem {
  name?: AttributeValue;
}
export const getReportDeviceData = async (tableName: string, uuid: string) => {
  const params = {
    TableName: tableName,
    IndexName: 'hospital_id-created_at-index',
    KeyConditionExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: uuid },
    },
  };

  try {
    const command = new QueryCommand(params);
    const hospitalDeviceData = await dbClient.send(command);

    if (!hospitalDeviceData.Items || hospitalDeviceData.Items.length === 0) {
      return [];
    }

    const deviceList: any[] = [];

    for (const data of hospitalDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          uuid: { S: data.device_id?.S || '' }, // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === APPROVED.toLowerCase()) {

          // Fetch latest patient name from deviceChangeLog
          const logParams = {
            TableName: 'deviceChangeLog',
            IndexName: 'device_id-created_at-index',
            KeyConditionExpression: 'device_id = :device_id',
            ExpressionAttributeValues: {
              ':device_id': { S: deviceData.Item.device_id?.S || '' }
            },
            ScanIndexForward: false,
            Limit: 1
          };
          const logResponse = await dbClient.send(new QueryCommand(logParams));
          const latestPatientName = logResponse.Items?.[0]?.patient_name?.S || data.patient_name?.S || '';

          deviceList.push({
            id: deviceData.Item.uuid?.S || '',
            device_id: deviceData.Item.device_id?.S || '',
            device_name: deviceData.Item.device_name?.S || '',
            patient_name: latestPatientName,
          });
      }
    }

    return deviceList;
  } catch (error) {
    console.error('Error fetching hospital device data:', error);
    throw error;
  }
};
export const getNurseReportDeviceData = async (tableName: string, userId: string) => {
  const params = {
    TableName: tableName,
    IndexName: 'user_id-created_at-index',
    KeyConditionExpression: 'user_id = :user_id',
    ExpressionAttributeValues: {
      ':user_id': { S: userId },
    },
  };

  try {
    const command = new QueryCommand(params);
    const nurseDeviceData = await dbClient.send(command);

    if (!nurseDeviceData.Items || nurseDeviceData.Items.length === 0) {
      return [];
    }
    const deviceList: any[] = [];

    for (const data of nurseDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          uuid: { S: data.device_id?.S || '' }, // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === APPROVED.toLowerCase()) {

          const logParams = {
            TableName: 'deviceChangeLog',
            IndexName: 'device_id-created_at-index',
            KeyConditionExpression: 'device_id = :device_id',
            ExpressionAttributeValues: {
              ':device_id': { S: deviceData.Item.device_id?.S || '' }
            },
            ScanIndexForward: false,
            Limit: 1
          };
          const logResponse = await dbClient.send(new QueryCommand(logParams));
          const latestPatientName = logResponse.Items?.[0]?.patient_name?.S || data.patient_name?.S || '';

          deviceList.push({
            id: deviceData.Item.uuid?.S || '',
            device_id: deviceData.Item.device_id?.S || '',
            device_name: deviceData.Item.device_name?.S || '',
            patient_name: latestPatientName,
          });
      }
    }

    return deviceList;
  } catch (error) {
    console.error('Error fetching nurse device data:', error);
    throw error;
  }
};

export const getIndividualDeviceData = async (tenantId: string) => {
  const params = {
    TableName: 'individual_device',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
     ':tenant_id': { S: tenantId },
    },
  };

  try {
    const command = new QueryCommand(params);
    const individualDeviceData = await dbClient.send(command);

    if (!individualDeviceData.Items || individualDeviceData.Items.length === 0) {
      return [];
    }

    const deviceList: any[] = [];

    for (const data of individualDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          'uuid': { S: data.device_id?.S ? data.device_id.S : '' },  // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));
      // Check if device_status is 'approved' before adding to the list
      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() == APPROVED.toLowerCase()) {
        const logParams = {
          TableName: 'deviceChangeLog',
          IndexName: 'device_id-created_at-index',
          KeyConditionExpression: 'device_id = :device_id',
          ExpressionAttributeValues: {
            ':device_id': { S: deviceData.Item.device_id?.S || '' }
          },
          ScanIndexForward: false,
          Limit: 1
        };
        const logResponse = await dbClient.send(new QueryCommand(logParams));
        const latestPatientName = logResponse.Items?.[0]?.patient_name?.S || data.patient_name?.S || '';

        deviceList.push({
          id: deviceData.Item.uuid?.S || '',            // Add uuid field
          device_id: deviceData.Item.device_id?.S || '',  // Add device_id field
          device_name: deviceData.Item.device_name?.S || '', // Add device_name field
          device_status: deviceData.Item.device_status?.S || '', // Add device_status field
          patient_name: latestPatientName,
          // Add any other fields you need from the device data
        });
      }
    }

    return deviceList;  // Return the final list of approved devices
  } catch (error) {
    console.error('Error fetching individual device data:', error);
    throw error;
  }
};
export const getHospitalInfusionDeviceData = async (tableName: string, uuid: string) => {
  const params = {
    TableName: tableName,
    IndexName: 'hospital_id-created_at-index',
    KeyConditionExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: uuid },
    },
  };

  try {
    const command = new QueryCommand(params);
    const hospitalDeviceData = await dbClient.send(command);

    if (!hospitalDeviceData.Items || hospitalDeviceData.Items.length === 0) {
      return [];
    }

    const deviceList: any[] = [];

    for (const data of hospitalDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          uuid: { S: data.device_id?.S || '' }, // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === APPROVED.toLowerCase()) {

          deviceList.push({
            id: deviceData.Item.uuid?.S || '',
            device_id: deviceData.Item.device_id?.S || '',
            device_name: deviceData.Item.device_name?.S || '',
            device_status: deviceData.Item.device_status?.S || '',
            status: deviceData.Item.status?.S || '',
          });
      }
    }

    return deviceList;
  } catch (error) {
    console.error('Error fetching hospital device data:', error);
    throw error;
  }
};
export const getNurseInfusionDeviceData = async (tableName: string, userId: string) => {
  const params = {
    TableName: tableName,
    IndexName: 'user_id-created_at-index',
    KeyConditionExpression: 'user_id = :user_id',
    ExpressionAttributeValues: {
      ':user_id': { S: userId },
    },
  };

  try {
    const command = new QueryCommand(params);
    const nurseDeviceData = await dbClient.send(command);

    if (!nurseDeviceData.Items || nurseDeviceData.Items.length === 0) {
      return [];
    }
    const deviceList: any[] = [];

    for (const data of nurseDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          uuid: { S: data.device_id?.S || '' }, // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));

      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() === APPROVED.toLowerCase()) {

          deviceList.push({
            id: deviceData.Item.uuid?.S || '',
            device_id: deviceData.Item.device_id?.S || '',
            device_name: deviceData.Item.device_name?.S || '',
            device_status: deviceData.Item.device_status?.S || '',
            status: deviceData.Item.status?.S || '',
          });
      }
    }

    return deviceList;
  } catch (error) {
    console.error('Error fetching nurse device data:', error);
    throw error;
  }
};

export const getIndividualInfusionDeviceData = async (tenantId: string) => {
  const params = {
    TableName: 'individual_device',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
     ':tenant_id': { S: tenantId },
    },
  };

  try {
    const command = new QueryCommand(params);
    const individualDeviceData = await dbClient.send(command);

    if (!individualDeviceData.Items || individualDeviceData.Items.length === 0) {
      return [];
    }

    const deviceList: any[] = [];

    for (const data of individualDeviceData.Items) {
      const getItemParams = {
        TableName: DEVICE_TABLE,
        Key: {
          'uuid': { S: data.device_id?.S ? data.device_id.S : '' },  // Safely access device_id
        },
      };

      const deviceData = await dbClient.send(new GetItemCommand(getItemParams));
      // Check if device_status is 'approved' before adding to the list
      if (deviceData.Item && deviceData.Item.status?.S?.toLowerCase() == APPROVED.toLowerCase()) {
        deviceList.push({
          id: deviceData.Item.uuid?.S || '',            // Add uuid field
          device_id: deviceData.Item.device_id?.S || '',  // Add device_id field
          device_name: deviceData.Item.device_name?.S || '', // Add device_name field
          device_status: deviceData.Item.device_status?.S || '',
          status: deviceData.Item.status?.S || '',
        });
      }
    }

    return deviceList;  // Return the final list of approved devices
  } catch (error) {
    console.error('Error fetching individual device data:', error);
    throw error;
  }
};

export async function deviceChangeLogInfusionData(deviceId: string) {
  const params = {
    TableName: 'deviceChangeLog',
    IndexName: 'device_id-created_at-index',
    KeyConditionExpression: 'device_id = :device_id',
    ExpressionAttributeValues: {
      ':device_id': { S: deviceId },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    if (response.Items && response.Items.length > 0) {
      return response.Items;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error querying deviceChangeLog:', error);
    throw error;
  }
}

export const fetchDeviceChangeLogInfusionData = async (
  deviceId: string,
  startDate: string,
  endDate: string
) => {
  const adjustedStartDate = new Date(startDate);
  adjustedStartDate.setHours(0, 0, 0, 0);

  const adjustedEndDate = new Date(endDate);
  adjustedEndDate.setHours(23, 59, 59, 999);

  const params: QueryCommandInput = {
    TableName: 'deviceChangeLog',
    IndexName: 'device_id-created_at-index',
    KeyConditionExpression: 'device_id = :device_id AND created_at BETWEEN :startDate AND :endDate',
    ExpressionAttributeValues: {
      ':device_id': { S: deviceId },
      ':startDate': { S: adjustedStartDate.toISOString() },
      ':endDate': { S: adjustedEndDate.toISOString() },
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    if (response.Items && response.Items.length > 0) {
      return response.Items;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching change log data:', error);
    throw new Error('Error fetching change log data');
  }
};

export async function hospitalNurse(hospitalId: string) {
  const params = {
    TableName: 'user_info',
    IndexName: 'hospital_id-created_at-index',
    KeyConditionExpression: 'hospital_id = :hospital_id',
    FilterExpression: 'deleted_at = :deleted_at AND #role = :role AND #status = :status',
    ExpressionAttributeValues: {
      ":hospital_id": { S: hospitalId },
      ':status': { S: APPROVED },
      ':role': { S: NURSE },
      ':deleted_at': { S: '' },
    },
    ExpressionAttributeNames: {
      '#status': 'status',
      '#role': 'role',
    },
  };

  try {
    let items: Nurse[] = [];
    let lastEvaluatedKey: any = null;

    do {
      const command = new QueryCommand({
        ...params,
        ExclusiveStartKey: lastEvaluatedKey,
      });
      const response = await dbClient.send(command);
      const filteredItems = (response.Items || [])
        .filter(item => !item.deleted_at?.S && item.role?.S == 'Nurse')
        .map(item => ({
          uuid: item.uuid?.S || '',
          tenant_id: item.tenant_id?.S || '',
          user_id: item.user_id?.S || '',
          name: item.name?.S ? capitalizeFirstLetter(item.name?.S) : '',
          nurse_id: item.nurse_id?.S || '',
        }));
      items.push(...filteredItems);
      lastEvaluatedKey = response.LastEvaluatedKey;
    } while (lastEvaluatedKey);

    return items;
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export async function getDeviceDetails(deviceId: string, hospital_id: string) {
  const params = {
    TableName: 'devices',
    Key: {
      uuid: { S: deviceId }
    },
  };

  try {
    // console.log('deviceId', deviceId);
    const command = new GetItemCommand(params);
    const response = await dbClient.send(command);
    // Log the response for debugging
    // console.log('GetItem Response:', response); // Log the response for debugging

    if (response.Item) {
      if (response.Item.status?.S?.toLowerCase() == APPROVED.toLowerCase()) {
        return {
          uuid: response.Item.uuid?.S || '',
          device_id: response.Item.device_id?.S || '',
          device_name: response.Item.device_name?.S || '',
          hospital_id: hospital_id,
          // Map other fields from the devices table
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching device details:', error);
    throw error;
  }
}


export async function hospitalTableUuid(tenantId: string) {
  const params = {
    TableName: 'hospitals',
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    if (response.Items && response.Items.length != 0) {
      return response.Items[0]
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

interface NurseDevice {
  id: string;
  device_uuid: string;
  device_name: string;
  device_id: string;
  nurse_name: string;
  nurse_id: string;
  nurse_uuid: string;
  user_id: string;
  tenant_id: string;
  created_at: string;
  created_at_time: string;
  hospital_id: string;
  has_control?: boolean;
  controlling_nurse_name?: string;
}

interface GroupedDeviceData {
  device_uuid: string;
  device_name: string;
  device_id: string;
  controlling_nurse_name?: string;
  hospital_id: string;
  created_at_time: string;
  nurses: NurseDevice[];
}

/**
 * Formats an ISO 8601 string to a readable date and time format.
 * @param isoString - The ISO 8601 string to format.
 * @returns A string in the format "DD/MM/YYYY HH:MM AM/PM".
 */
const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  return date.toLocaleString('en-US', options);
};

export async function nurseDeviceList(uuid: string): Promise<GroupedDeviceData[]> {
  let deviceData: { [key: string]: NurseDevice[] } = {};
  let lastEvaluatedKey = undefined;

  try {
    do {
      const params: QueryCommandInput = {
        TableName: 'nurse_device',
        IndexName: 'hospital_id-created_at-index',
        KeyConditionExpression: 'hospital_id = :hospital_id',
        FilterExpression: 'deleted_at = :deleted_at',
        ExpressionAttributeValues: {
          ':hospital_id': { S: uuid },
          ':deleted_at': { S: '' }
        },
        ExclusiveStartKey: lastEvaluatedKey
      };

      const command = new QueryCommand(params);
      const response = await dbClient.send(command);

      if (response.Items && response.Items.length > 0) {
        const result = await Promise.all(
          response.Items
            .filter(item => !item.deleted_at?.S)
            .map(async (item) => {
              const createdAT = item.created_at?.S ? formatDateDMY(item.created_at.S) : '';
              const createdDateTime = item.created_at?.S ? formatDateTime(item.created_at.S) : '';
              const nurse = item.user_id?.S ? await userDetail(item.user_id.S) : '';
              const device = item.device_id?.S ? await getDataUuid(DEVICE_TABLE, item.device_id.S) : '';
              if (!nurse || !nurse.uuid.S || !nurse.name.S || !nurse.nurse_id.S) {
                let attributeNames = {
                  '#deleted_at': 'deleted_at',
                  '#deleted_by': 'deleted_by',
                  '#modified_by': 'modified_by',
                  '#modified_at': 'modified_at'
                };
        
                const formattedDateTime = generateUniqueTimestamp();
                let attributeValues = {
                ':deleted_at': { S: formattedDateTime },
                ':deleted_by': { S: item.tenant_id.S },
                ':modified_by': { S: item.tenant_id.S },
                ':modified_at': { S: formattedDateTime },
                };
            
                let updateExpression =
                'SET #deleted_at = :deleted_at, #deleted_by = :deleted_by, #modified_by = :modified_by, #modified_at = :modified_at';
        
                if(item?.uuid?.S) {
                    await updateUser(NURSE_DEVICE_TABLE, item?.uuid?.S, attributeNames, attributeValues, updateExpression);
                }

                return null;
              }
              const nurseDevice: NurseDevice = {
                id: item.uuid?.S || '',
                device_uuid: item.device_id?.S ? item.device_id?.S : '',
                device_name: device && device.device_name?.S ? device.device_name.S : '',
                device_id: device && device.device_id?.S ? device.device_id.S : '',
                nurse_name: nurse && nurse.name?.S ? capitalizeFirstLetter(nurse.name.S) : '',
                nurse_id: nurse && nurse.nurse_id?.S ? nurse.nurse_id?.S : '',
                nurse_uuid: nurse && nurse.uuid?.S ? nurse.uuid.S : '',
                user_id: nurse && nurse.user_id?.S ? nurse.user_id.S : '',
                tenant_id: nurse && nurse.tenant_id?.S ? nurse.tenant_id.S : '',
                created_at: createdAT,
                created_at_time: createdDateTime,
                hospital_id: item.hospital_id?.S || '',
                has_control: item.has_control?.BOOL || false,
              };

              return nurseDevice;
            })
        )

        const filteredResult = result.filter((nurseDevice): nurseDevice is NurseDevice => nurseDevice !== null);

        filteredResult.forEach((nurseDevice) => {
          if (!deviceData[nurseDevice.device_uuid]) {
            deviceData[nurseDevice.device_uuid] = [];
          }
          deviceData[nurseDevice.device_uuid].push(nurseDevice);
        });
      }

      lastEvaluatedKey = response.LastEvaluatedKey;
    } while (lastEvaluatedKey);

    // Group data by device and set controlling nurse name
    const groupedData: GroupedDeviceData[] = [];
    for (const deviceId in deviceData) {
      const nurses = deviceData[deviceId];
      let controllingNurse = nurses.find(nurse => nurse.has_control);
      // console.log('controllingNurse', controllingNurse)

      groupedData.push({
        device_uuid: deviceId,
        device_name: nurses[0]?.device_name || '',
        device_id: nurses[0]?.device_id || '',
        controlling_nurse_name: controllingNurse ? controllingNurse.nurse_name : '',
        hospital_id: nurses[0]?.hospital_id || '',
        created_at_time: nurses[0]?.created_at_time || '',
        nurses
      });
    }

    return groupedData;

  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}

export const fetchDeviceChangeLogData = async (
  deviceId: string,
  startDate: string,
  endDate: string,
  patientName?: string
) => {
  // Append time to the endDate for full day inclusion
  const adjustedEndDate = new Date(endDate);
  adjustedEndDate.setHours(23, 59, 59); // Set time to the end of the day

  const params: QueryCommandInput = {
    TableName: 'deviceChangeLog',
    IndexName: 'device_id-created_at-index',
    KeyConditionExpression: 'device_id = :device_id AND created_at BETWEEN :startDate AND :endDate',
    ExpressionAttributeValues: {
      ':device_id': { S: deviceId }, 
      ':startDate': { S: startDate },
      ':endDate': { S: adjustedEndDate.toISOString() }, // Set the ISO string with time information
    },
  };

  if (patientName && params.ExpressionAttributeValues) {
    params.FilterExpression = 'patient_name = :patient_name';
    params.ExpressionAttributeValues[':patient_name'] = { S: patientName };
  }

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response.Items || [];
  } catch (error) {
    console.error('Error fetching change log data:', error);
    throw new Error('Error fetching change log data');
  }
};

export const fetchDeviceChangeLogDataByPatientName = async (
  patientName: string,
  startDate: string,
  endDate: string
) => {
  // Append time to the endDate for full day inclusion
  const adjustedEndDate = new Date(endDate);
  adjustedEndDate.setHours(23, 59, 59); // Set time to the end of the day

  const params = {
    TableName: 'deviceChangeLog',
    FilterExpression: 'patient_name = :patient_name AND created_at BETWEEN :startDate AND :endDate',
    ExpressionAttributeValues: {
      ':patient_name': { S: patientName },
      ':startDate': { S: startDate },
      ':endDate': { S: adjustedEndDate.toISOString() },
    },
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    return response.Items || [];
  } catch (error) {
    console.error('Error fetching change log data by patient name:', error);
    throw new Error('Error fetching change log data by patient name');
  }
};

export const fetchAllPatientsForHospital = async (deviceIds: string[]) => {
  if (deviceIds.length === 0) return [];

  // We can't use Query with multiple partition keys easily, so we Scan with a FilterExpression
  // For large tables, this should be optimized, but for now we filter by device_id
  const params = {
    TableName: 'deviceChangeLog',
    FilterExpression: '#did IN (' + deviceIds.map((_, i) => ':id' + i).join(',') + ')',
    ExpressionAttributeNames: {
        '#did': 'device_id'
    },
    ExpressionAttributeValues: deviceIds.reduce((acc, id, i) => ({
      ...acc,
      [':id' + i]: { S: id },
    }), {}),
    ProjectionExpression: 'patient_name, paitent_name',
  };

  try {
    const command = new ScanCommand(params);
    const response = await dbClient.send(command);
    const items = response.Items || [];
    const patients = items
      .map((item) => {
          const name = item.patient_name?.S || (item as any).paitent_name?.S;
          return name;
      })
      .filter((name): name is string => !!name && name.trim() !== '');
    return Array.from(new Set(patients));
  } catch (error) {
    console.error('Error fetching all patients for devices:', error);
    return [];
  }
};



export async function nurseDeviceUpdate(tableName: string, id: string, tenant_id: string, deviceUuid: string, userId: string) {
  const formattedDateTime = generateUniqueTimestamp();
  const attributeNames = {
    '#device_id': 'device_id',
    '#user_id': 'user_id',
    '#modified_by': 'modified_by',
    '#modified_at': 'modified_at',
  };

  const attributeValues = {
    ':device_id': { S: deviceUuid },
    ':user_id': { S: userId },
    ':modified_by': { S: tenant_id },
    ':modified_at': { S: formattedDateTime },
  };

  const updateExpression =
    'SET #device_id = :device_id, #user_id = :user_id, #modified_by = :modified_by, #modified_at = :modified_at';

  const input: UpdateItemCommandInput = {
    TableName: tableName, // Replace with your table name
    Key: {
      uuid: { S: id }, // Replace with your primary key
    },
    ExpressionAttributeNames: attributeNames,
    ExpressionAttributeValues: attributeValues,
    UpdateExpression: updateExpression,
    ReturnValues: "ALL_NEW"
  };

  const command = new UpdateItemCommand(input);

  try {
    const response = await dbClient.send(command);
    // console.log("Item updated successfully:", response);
    // return {
    //   statusCode: 200,
    //   body: JSON.stringify({ message: "Item updated successfully", updatedItem: response.Attributes }),
    // };
  } catch (error: any) {
    console.error("Error updating item:", error);

    let errorMessage: string;

    switch (error.name) {
      case 'ConditionalCheckFailedException':
        errorMessage = 'Condition check failed';
        break;
      case 'ProvisionedThroughputExceededException':
        errorMessage = 'Throughput limit exceeded';
        break;
      case 'ResourceNotFoundException':
        errorMessage = 'Requested resource not found';
        break;
      case 'ItemCollectionSizeLimitExceededException':
        errorMessage = 'Item collection size limit exceeded';
        break;
      case 'TransactionConflictException':
        errorMessage = 'Transaction conflict occurred';
        break;
      case 'RequestLimitExceeded':
        errorMessage = 'Request limit exceeded';
        break;
      case 'InternalServerError':
        errorMessage = 'Internal server error occurred';
        break;
      default:
        errorMessage = `Unknown error: ${error.message}`;
    }

    throw errorMessage
  }
};




export const generateDeviceReportPDF = async (params: { device_id?: string; patient_name?: string; start_date: string; end_date: string; tenant_id: string }) => {
  try {
    const response = await fetch(GENERATE_REPORT_PDF_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('PDF generation result:', data);
    return data;
  } catch (error) {
    console.error('Error generating PDF report:', error);
    throw error;
  }
};
